<template>
  <div class="container-fluid dashboard body-color" :class="{'board-view-container': isOnBoardTab}">
    <b-tabs content-class="mt-3" @input="onTabChange" v-model="activeTab">
      <b-tab id="dashboard-tab-home" lazy>
        <template v-slot:title>
          <i class="fas fa-house-user mr-1" style="font-size: 12px;"></i>
          <strong>Home</strong>
        </template>
        <Summary />
        <Highlights />
        <Filters />
        <EditableTable />
      </b-tab>
      <b-tab id="dashboard-tab-board-view">
        <template v-slot:title>
          <strong>Job Board&nbsp;</strong>
          <span class="badge badge-warning ml-1" style="font-size: 10px; background-color: #ff8c00;">new</span>
        </template>
        <DashboardBoard />
      </b-tab>
      <b-tab id="dashboard-tab-technician-report" lazy>
        <template v-slot:title>
          <!-- <span class="badge badge-warning">New</span> &nbsp; -->
          <strong>Technician Reports</strong>
        </template>
        <TechinicianDashboard />
      </b-tab>

      <b-tab id="dashboard-tab-manager-report" lazy v-if="$isDev">
        <template v-slot:title>
          <!-- <span class="badge badge-warning">New</span> &nbsp; -->
          <strong>Manager Reports</strong>
        </template>
        <ManagerReports />
      </b-tab>

      <b-tab id="dashboard-tab-financial-overview" lazy>
        <template v-slot:title>
          <strong>Financial Overview</strong>
        </template>
        <FinancialOverview />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
  import Summary from "@/components/dashboard/Summary.vue";
  import Highlights from "@/components/dashboard/Highlights.vue";
  import Filters from "@/components/dashboard/Filters.vue";
  import EditableTable from "@/components/table/EditableTable.vue";
  import EVENTS from "@/constants/events";
  import TechinicianDashboard from "@/components/dashboard/technician-dashboard.vue";
  import ManagerReports from "@/components/dashboard/ManagerReports.vue";
  import FinancialOverview from "@/components/dashboard/FinancialOverview.vue";
  import DashboardBoard from "@/components/dashboard/DashboardBoard.vue";

  export default {
    name: "dashboard",
    components: {
      Summary,
      Highlights,
      Filters,
      EditableTable,
      TechinicianDashboard,
      ManagerReports,
      FinancialOverview,
      DashboardBoard,
    },
    data() {
      return {
        EVENT_ACTION: {},
        scrollThrottle: null,
        activeTab: 0, // Will be set based on route in created()
      };
    },
    computed: {
      isOldDashboardEnabled() {
        return process.env.VUE_APP_SHOW_OLD_DASHBOARD === "true";
      },
      filters() {
        return this.$store.getters.getFilter;
      },
      isOnBoardTab() {
        return this.activeTab === 1; // Board View is now the 2nd tab (index 1)
      },
      // Computed property to determine the initial active tab based on route
      initialActiveTab() {
        if (this.$route.path.startsWith('/board')) {
          return 1; // Board tab
        }
        return 0; // Default to Home tab
      },
    },
    watch: {
      // Watch for route changes to update active tab
      '$route'(to, from) {
        const newActiveTab = to.path.startsWith('/board') ? 1 : 0;
        if (newActiveTab !== this.activeTab) {
          this.activeTab = newActiveTab;
        }
      }
    },
    // Add navigation guards to ensure data loads when navigating back to dashboard
    beforeRouteEnter(to, from, next) {
      next(vm => {
        // This runs after the component is created and mounted
        // Ensure fresh data when entering dashboard from another route
        if (from && from.name !== 'dashboard' && from.name !== 'index' && from.name !== 'board') {
          vm.loadDashboard();
        }
      });
    },
    beforeRouteUpdate(to, from, next) {
      // Handle route updates within the same component
      next();
      // Load data if switching between dashboard routes
      this.loadDashboard();
    },
    methods: {
      async loadDashboard() {
        // Always reset to page 1 when loading dashboard to ensure fresh data
        this.$store.commit("setPage", 1);
        
        if ((this.filters && Object.keys(this.filters).length === 0) || !this.filters) {
          await this.$store.dispatch("getProjectsAction");
        } else {
          const currentResultsPerPage = this.$store.getters.getResultsPerPage;
          if ((this.filters && this.filters.maximumRows && this.filters.maximumRows !== currentResultsPerPage) || (this.filters && !this.filters.maximumRows)) {
            // Update filter with current results per page setting
            this.filters.maximumRows = currentResultsPerPage;
            this.$root.$emit("filters-timer", true);
            await this.$store.dispatch("getNextFilterResults", this.filters);
          } else {
            // If filters exist but are up to date, still reload to ensure fresh data
            await this.$store.dispatch("getFilterResults", this.filters);
          }
        }
      },
      handleScroll() {
        // Throttle scroll events to improve performance on slower computers
        if (this.scrollThrottle) return;
        
        this.scrollThrottle = setTimeout(() => {
          // Make scroll detection more conservative for slower computers
          // Only trigger at 85% scrolled instead of 50%
          const scrollPosition = window.innerHeight + window.scrollY;
          const documentHeight = document.body.offsetHeight;
          const scrollPercentage = scrollPosition / documentHeight;
          
          // Only load more if user has scrolled at least 85% down
          if (scrollPercentage >= 0.85) {
            this.loadDashboard();
          }
          
          this.scrollThrottle = null;
        }, 150); // Throttle to once every 150ms
      },
      onTabChange(tabIndex) {
        this.activeTab = tabIndex;
        
        // Handle routing for board tab
        if (tabIndex === 1) { // Board tab is index 1
          // If we're not already on a board route, navigate to board
          if (!this.$route.path.startsWith('/board')) {
            this.$router.push('/board');
          }
        } else {
          // For other tabs, ensure we're on dashboard route
          if (this.$route.path.startsWith('/board')) {
            this.$router.push('/dashboard');
          }
        }
      },
    },
    async mounted() {
      //ONLY TRACK EVENTS IN PRODUCTION
      // if (process.env.VUE_APP_ENVIRONMENT === "production") {
      //   window.analytics.page("Dahsboard");
      // }
      
      window.addEventListener("scroll", this.handleScroll);
      
      // Ensure data loads when component is mounted (e.g., when navigating back)
      await this.loadDashboard();
    },
    async created() {
      // Set initial active tab based on current route
      this.activeTab = this.initialActiveTab;
      
      // Initialize results per page from localStorage FIRST before any data loading
      const storedResultsPerPage = localStorage.getItem('dashboard-results-per-page');
      if (storedResultsPerPage) {
        await this.$store.dispatch('updateResultsPerPage', parseInt(storedResultsPerPage, 10));
      }
      
      this.$store.commit("setPage", 1);
      await this.loadDashboard();

      this.$store.dispatch("showfullNavigation", true);

      //Import event constants and set local variable
      this.EVENT_ACTION = EVENTS;

      //Subscribe to dashboard refresh action
      this.$root.$on(this.EVENT_ACTION.DASHBOARD.Update, this.loadDashboard);
    },
    beforeDestroy() {
      //UnSubscribe to dashboard refresh action
      this.$root.$off(this.EVENT_ACTION.DASHBOARD.Update, this.loadDashboard);
      window.removeEventListener("scroll", this.handleScroll);
      
      // Clean up scroll throttle timeout
      if (this.scrollThrottle) {
        clearTimeout(this.scrollThrottle);
      }
    },
  };
</script>

<style>
.board-view-container {
  height: 100vh;
  overflow: hidden;
}

.board-view-container .tab-content {
  height: calc(100vh - 120px);
  overflow: hidden;
}

.board-view-container .mt-3 {
  margin-top: 0 !important;
  height: 100%;
}
</style>
